import { useState, MouseEvent, ChangeEvent, useEffect } from "react";
import { BoxProps, TablePagination } from "@mui/material";
import { AppConstant } from "@/const";
import { LodashUtils } from "@/utils";
import { PaginationType } from "@/types/common";
import AppTableHead from "./AppTableHead";
import AppTableBody from "./AppTableBody";
import { GridColDef, RowType } from "./AppTable";
import { AppTableRoot, BoxTable, AppTableContainer } from "./AppTable.style";

type AppTableProps<T> = BoxProps & {
  data: T[];
  columns: GridColDef<T>[];
  pagination?: PaginationType;
  onChangePagination?: (pagination: PaginationType) => void;
  hasCheckbox?: boolean;
  onSelectedRequest: (selectedRequests: string[]) => void;
};

function AppTable<T extends RowType>(props: AppTableProps<T>) {
  const { data, columns, onChangePagination, pagination, hasCheckbox = true, onSelectedRequest } = props;
  const [selectedListId, setSelectedListId] = useState<string[]>([]);
  const [pagingTable, setPagingTable] = useState({
    ...AppConstant.DEFAULT_PAGINATION,
    ...pagination,
  });

  const handleChangePagination = (newPaging: PaginationType) => {
    setPagingTable(newPaging);

    if (onChangePagination) {
      onChangePagination(newPaging);
    }
  };

  const handleChangePage = (_: MouseEvent<HTMLButtonElement> | null, page: number) => {
    handleChangePagination({ ...pagingTable, page: page + 1 });
  };

  const handleChangeSize = (event: ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(event.target.value, 10);
    handleChangePagination({ ...pagingTable, page: 1, size });
  };

  const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = data.map((rowData: T) => rowData.uid);
      setSelectedListId(newSelected);
      onSelectedRequest(newSelected);
      return;
    }
    setSelectedListId([]);
    onSelectedRequest([]);
  };

  const handleSelectRow = (id: string) => {
    const selectedIndex = selectedListId.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedListId, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedListId.slice(1));
    } else if (selectedIndex === selectedListId.length - 1) {
      newSelected = newSelected.concat(selectedListId.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedListId.slice(0, selectedIndex), selectedListId.slice(selectedIndex + 1));
    }
    setSelectedListId(newSelected);
    onSelectedRequest(newSelected);
  };

  useEffect(() => {
    if (!LodashUtils.isEmpty(pagination) && !LodashUtils.isEqual(pagination, pagingTable)) {
      setPagingTable(pagination);
    }
  }, [pagination]);

  return (
    <BoxTable>
      <AppTableContainer>
        <AppTableRoot stickyHeader aria-label="sticky table">
          <AppTableHead
            selectedNum={selectedListId.length}
            rowCount={data.length}
            handleSelectAllClick={handleSelectAllClick}
            columns={columns}
            hasCheckbox={hasCheckbox}
          />
          <AppTableBody
            data={data}
            columns={columns}
            selectedList={selectedListId}
            onSelectRow={handleSelectRow}
            hasCheckbox={hasCheckbox}
          />
        </AppTableRoot>
      </AppTableContainer>
      <TablePagination
        rowsPerPageOptions={AppConstant.ARR_PAGINATION}
        component="div"
        count={pagingTable.total}
        rowsPerPage={pagingTable.size}
        page={pagingTable.page - 1}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeSize}
      />
    </BoxTable>
  );
}
export default AppTable;
